import { useBooleanFeature } from './useFeatures';

/**
 * Hook to get pricing based on A/B test
 *
 * Pricing A/B Test v2:
 * - 50% of users (feature_pricing_19pm_v2 = false): See $14/mo on trial modal, $19/mo everywhere else
 * - 50% of users (feature_pricing_19pm_v2 = true): See $19/mo everywhere
 *
 * Note: feature_pricing_19pm (v1) is deprecated due to corrupted data - users were assigned
 * to the $19 group but were charged $14 due to a field name mismatch in the API call.
 *
 * Usage:
 * - For trial modal: use getTrialModalPrice()
 * - For all other places: use getStandardPrice() or just use 19 directly
 */
export const usePricing = () => {
  const { getFeature } = useBooleanFeature();

  // Get the user's pricing variant (using v2 flag for fresh experiment data)
  const show19Price = getFeature('feature_pricing_19pm_v2');

  /**
   * Get the price to show on the trial modal
   * Returns $19 if user is in test group, $14 if user is in control group
   */
  const getTrialModalPrice = () => {
    return show19Price ? 19 : 14; // Test group: $19, Control group: $14
  };

  /**
   * Get the price to show everywhere else (website, upgrade modal, etc.)
   * Always returns $19 for all users
   */
  const getStandardPrice = () => {
    return 19;
  };

  /**
   * Get yearly price based on monthly price
   */
  const getYearlyPrice = (monthlyPrice: number) => {
    return monthlyPrice * 12;
  };

  return {
    getTrialModalPrice,
    getStandardPrice,
    getYearlyPrice,
    show19Price, // Expose the flag for conditional rendering if needed
  };
};

export default usePricing;
